import { useState, useEffect, useMemo } from 'react';
import {
  getAllWorkoutsWithUsers,
  type FeedUser,
} from '../../infrastructure/repositories/leaderboardRepository';
import { adminDeleteWorkout } from '../../infrastructure/repositories/workoutRepository';
import { updateUserScoringSex, deleteUser } from '../../infrastructure/repositories/userRepository';
import {
  getMultiplierOverrides,
  setMultiplierOverride,
  clearMultiplierOverride,
} from '../../infrastructure/repositories/exerciseConfigRepository';
import { formatLoadAndReps } from '../../domain/workouts/describeLift';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '../../domain/scoring/categories';
import {
  getExercisesByCategory,
  applyMultiplierOverrides,
  shippedMultiplier,
} from '../../domain/scoring/exerciseCatalog';
import type { Workout } from '../../domain/workouts/types';
import type { Category } from '../../domain/scoring/types';
import type { ScoringSex } from '../../domain/users/types';
import './AdminPanel.css';

/**
 * NOT A SECRET. This ships in the JS bundle, where anyone who opens devtools can
 * read it, and the Firestore rules do not check it - a determined person can
 * delete workouts with or without this panel. It gates the UI so the tool is not
 * sitting open in Settings, nothing more. Real protection needs authentication
 * plus rules keyed on the signed-in uid; see docs/SECURITY.md.
 */
const ADMIN_PASSCODE = '214c6732aa';

const PAGE_SIZE = 100;

/** Matches the range the Firestore rules accept for a stored multiplier. */
const MIN_MULTIPLIER = 0.1;
const MAX_MULTIPLIER = 5;

/** "Aug 4, 2026" - parsed from parts so it does not read a day early west of UTC. */
function formatDate(workoutDate: string): string {
  const [year, month, day] = workoutDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function sexLabel(sex: string | undefined): string {
  if (sex === 'male') return 'M';
  if (sex === 'female') return 'F';
  return '?';
}

type SectionKey = 'people' | 'multipliers' | 'lifts';

export function AdminPanel() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState(false);

  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [users, setUsers] = useState<Record<string, FeedUser>>({});
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // The lift list is long, so the two editors above it start folded. Everything
  // stays one tap away without burying the list people came here for.
  const [open, setOpen] = useState<Record<SectionKey, boolean>>({
    people: false,
    multipliers: false,
    lifts: true,
  });

  const [personFilter, setPersonFilter] = useState('all');
  const [visible, setVisible] = useState(PAGE_SIZE);
  /** Delete is two-step: the first tap arms this row, the second commits. */
  const [armedId, setArmedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [armedUserId, setArmedUserId] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  /** Text being typed into a multiplier box, before it is committed. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingExerciseId, setSavingExerciseId] = useState<string | null>(null);

  useEffect(() => {
    if (!isUnlocked) return;
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setLoadError(null);
      try {
        const [data, loadedOverrides] = await Promise.all([
          getAllWorkoutsWithUsers(),
          getMultiplierOverrides(),
        ]);
        if (!cancelled) {
          setWorkouts(data.workouts);
          setUsers(data.users);
          setOverrides(loadedOverrides);
        }
      } catch (error) {
        console.error('Admin load failed:', error);
        if (!cancelled) setLoadError('Could not load workouts.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [isUnlocked]);

  /** How many lifts each person has, and how many disagree with their profile. */
  const liftStats = useMemo(() => {
    const stats: Record<string, { total: number; mismatched: number }> = {};
    for (const w of workouts) {
      const stat = (stats[w.userId] ??= { total: 0, mismatched: 0 });
      stat.total += 1;
      const profileSex = users[w.userId]?.scoringSex;
      if (profileSex && w.scoringSexSnapshot && profileSex !== w.scoringSexSnapshot) {
        stat.mismatched += 1;
      }
    }
    return stats;
  }, [workouts, users]);

  /**
   * Everyone with a profile, not just everyone who has lifted. A duplicate or
   * abandoned profile has no workouts at all, and those are exactly the ones
   * worth spotting here.
   */
  const people = useMemo(
    () =>
      Object.entries(users)
        .map(([id, u]) => ({ id, ...u }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [users],
  );

  const filtered = useMemo(
    () => (personFilter === 'all' ? workouts : workouts.filter((w) => w.userId === personFilter)),
    [workouts, personFilter],
  );

  const toggle = (key: SectionKey) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleUnlock = (e: React.FormEvent) => {
    e.preventDefault();
    if (passcode === ADMIN_PASSCODE) {
      setIsUnlocked(true);
      setPasscode('');
      setPasscodeError(false);
    } else {
      setPasscodeError(true);
    }
  };

  const handleDelete = async (workout: Workout) => {
    setDeletingId(workout.id);
    try {
      await adminDeleteWorkout(workout.id);
      // Drop it locally rather than refetching everything.
      setWorkouts((prev) => prev.filter((w) => w.id !== workout.id));
      setArmedId(null);
    } catch (error) {
      console.error('Admin delete failed:', error);
      setLoadError(`Could not delete that lift: ${(error as Error).message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleSexChange = async (userId: string, scoringSex: ScoringSex) => {
    setSavingUserId(userId);
    setLoadError(null);
    try {
      await updateUserScoringSex(userId, scoringSex);
      setUsers((prev) => ({ ...prev, [userId]: { ...prev[userId], scoringSex } }));
    } catch (error) {
      console.error('Admin sex change failed:', error);
      setLoadError(`Could not change that profile: ${(error as Error).message}`);
    } finally {
      setSavingUserId(null);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    setDeletingUserId(userId);
    setLoadError(null);
    try {
      await deleteUser(userId);
      // Remove from local state
      setUsers((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      // Also remove their workouts from local state
      setWorkouts((prev) => prev.filter((w) => w.userId !== userId));
      setArmedUserId(null);
    } catch (error) {
      console.error('Admin user delete failed:', error);
      setLoadError(`Could not delete that user: ${(error as Error).message}`);
    } finally {
      setDeletingUserId(null);
    }
  };

  /** Commits a typed multiplier. An empty box, or the shipped value, clears it. */
  const commitMultiplier = async (exerciseId: string) => {
    const draft = drafts[exerciseId];
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[exerciseId];
      return next;
    });
    if (draft === undefined) return;

    const shipped = shippedMultiplier(exerciseId);
    const trimmed = draft.trim();
    const parsed = trimmed === '' ? shipped : Number(trimmed);

    if (parsed === undefined || !Number.isFinite(parsed)) return;
    if (parsed < MIN_MULTIPLIER || parsed > MAX_MULTIPLIER) {
      setLoadError(`Multiplier must be between ${MIN_MULTIPLIER} and ${MAX_MULTIPLIER}.`);
      return;
    }
    if (parsed === (overrides[exerciseId] ?? shipped)) return;

    setSavingExerciseId(exerciseId);
    setLoadError(null);
    try {
      const next = { ...overrides };
      if (parsed === shipped) {
        await clearMultiplierOverride(exerciseId);
        delete next[exerciseId];
      } else {
        await setMultiplierOverride(exerciseId, parsed);
        next[exerciseId] = parsed;
      }
      setOverrides(next);
      // Apply immediately so a lift logged on this device right now is scored
      // with the new number, without waiting for a reload.
      applyMultiplierOverrides(next);
    } catch (error) {
      console.error('Admin multiplier change failed:', error);
      setLoadError(`Could not save that multiplier: ${(error as Error).message}`);
    } finally {
      setSavingExerciseId(null);
    }
  };

  if (!isUnlocked) {
    return (
      <form className="admin__gate" onSubmit={handleUnlock}>
        <label className="sr-only" htmlFor="admin-passcode">
          Admin password
        </label>
        <input
          id="admin-passcode"
          type="password"
          value={passcode}
          onChange={(e) => {
            setPasscode(e.target.value);
            setPasscodeError(false);
          }}
          placeholder="Password"
          autoComplete="off"
        />
        <button type="submit" className="admin__unlock">
          Unlock
        </button>
        {passcodeError && <span className="admin__error">Wrong password.</span>}
      </form>
    );
  }

  if (isLoading) return <p className="admin__muted">Loading...</p>;

  const overrideCount = Object.keys(overrides).length;

  return (
    <div className="admin">
      {loadError && <p className="admin__error">{loadError}</p>}

      {/* ── People ─────────────────────────────────────────────────────────── */}
      <button
        type="button"
        className="admin__section"
        onClick={() => toggle('people')}
        aria-expanded={open.people}
      >
        <span className="admin__section-name">People</span>
        <span className="admin__section-meta">{people.length}</span>
        <span className="admin__chevron">{open.people ? '−' : '+'}</span>
      </button>

      {open.people && (
        <ul className="admin__list">
          {people.map((p) => {
            const stat = liftStats[p.id];
            return (
              <li key={p.id} className="admin__row">
                <div className="admin__info">
                  <div className="admin__line1">
                    <span className="admin__name">{p.displayName}</span>
                  </div>
                  {p.normalizedEmail && <div className="admin__line2">{p.normalizedEmail}</div>}
                  <div className="admin__line3">
                    <span>{stat?.total ?? 0} lifts</span>
                    {stat?.mismatched ? (
                      <span className="admin__warn">
                        {stat.mismatched} scored{' '}
                        {sexLabel(p.scoringSex === 'male' ? 'female' : 'male')}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="admin__sexpicker">
                  {(['male', 'female'] as ScoringSex[]).map((sex) => (
                    <button
                      key={sex}
                      type="button"
                      className={`admin__sexbtn ${p.scoringSex === sex ? 'admin__sexbtn--on' : ''}`}
                      onClick={() => handleSexChange(p.id, sex)}
                      disabled={savingUserId === p.id || p.scoringSex === sex}
                      aria-label={`Set ${p.displayName} to ${sex}`}
                    >
                      {sexLabel(sex)}
                    </button>
                  ))}
                </div>

                {armedUserId === p.id ? (
                  <div className="admin__confirm">
                    <button
                      type="button"
                      className="admin__delete admin__delete--confirm"
                      onClick={() => handleDeleteUser(p.id)}
                      disabled={deletingUserId === p.id}
                    >
                      {deletingUserId === p.id ? '...' : 'Sure?'}
                    </button>
                    <button
                      type="button"
                      className="admin__cancel"
                      onClick={() => setArmedUserId(null)}
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="admin__delete"
                    onClick={() => setArmedUserId(p.id)}
                    aria-label={`Delete ${p.displayName}`}
                  >
                    ×
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* ── Multipliers ────────────────────────────────────────────────────── */}
      <button
        type="button"
        className="admin__section"
        onClick={() => toggle('multipliers')}
        aria-expanded={open.multipliers}
      >
        <span className="admin__section-name">Multipliers</span>
        <span className="admin__section-meta">
          {overrideCount > 0 ? `${overrideCount} changed` : 'all default'}
        </span>
        <span className="admin__chevron">{open.multipliers ? '−' : '+'}</span>
      </button>

      {open.multipliers && (
        <div className="admin__multipliers">
          <p className="admin__muted">
            Changes apply to lifts logged from now on. Every lift already logged keeps the
            multiplier it was scored with, so nobody&rsquo;s history moves.
          </p>
          {CATEGORY_ORDER.map((category: Category) => (
            <div key={category}>
              <h4 className="admin__subhead">{CATEGORY_LABELS[category]}</h4>
              <ul className="admin__list">
                {getExercisesByCategory(category)
                  .filter((ex) => ex.isActive)
                  .map((ex) => {
                    const shipped = shippedMultiplier(ex.id) ?? ex.normalizationMultiplier;
                    const current = overrides[ex.id] ?? shipped;
                    const isOverridden = ex.id in overrides;
                    return (
                      <li key={ex.id} className="admin__row admin__row--mult">
                        <div className="admin__info">
                          <div className="admin__line2">
                            {ex.displayName}
                            {ex.isCompetitionLift && <span className="admin__star">★</span>}
                          </div>
                          {isOverridden && (
                            <div className="admin__line3">
                              <span className="admin__warn">was {shipped.toFixed(2)}×</span>
                            </div>
                          )}
                        </div>
                        <input
                          className={`admin__mult ${isOverridden ? 'admin__mult--set' : ''}`}
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min={MIN_MULTIPLIER}
                          max={MAX_MULTIPLIER}
                          value={drafts[ex.id] ?? current.toFixed(2)}
                          disabled={savingExerciseId === ex.id}
                          aria-label={`${ex.displayName} multiplier`}
                          onChange={(e) =>
                            setDrafts((prev) => ({ ...prev, [ex.id]: e.target.value }))
                          }
                          onBlur={() => void commitMultiplier(ex.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                            if (e.key === 'Escape') {
                              setDrafts((prev) => {
                                const next = { ...prev };
                                delete next[ex.id];
                                return next;
                              });
                            }
                          }}
                        />
                      </li>
                    );
                  })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* ── Lifts ──────────────────────────────────────────────────────────── */}
      <button
        type="button"
        className="admin__section"
        onClick={() => toggle('lifts')}
        aria-expanded={open.lifts}
      >
        <span className="admin__section-name">Lifts</span>
        <span className="admin__section-meta">{workouts.length}</span>
        <span className="admin__chevron">{open.lifts ? '−' : '+'}</span>
      </button>

      {open.lifts && (
        <>
          <div className="admin__controls">
            <div className="admin__select">
              <label className="sr-only" htmlFor="admin-person">
                Person
              </label>
              <select
                id="admin-person"
                value={personFilter}
                onChange={(e) => {
                  setPersonFilter(e.target.value);
                  setVisible(PAGE_SIZE);
                  setArmedId(null);
                }}
              >
                <option value="all">Everyone ({workouts.length})</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                  </option>
                ))}
              </select>
            </div>
            <span className="admin__count">{filtered.length} lifts</span>
          </div>

          {filtered.length === 0 ? (
            <p className="admin__muted">No lifts.</p>
          ) : (
            <>
              <ul className="admin__list">
                {filtered.slice(0, visible).map((w) => {
                  const user = users[w.userId];
                  // The lift was scored with its own snapshot, which can differ
                  // from the profile - exactly the case worth spotting here.
                  const snapshot = w.scoringSexSnapshot;
                  const mismatch = user?.scoringSex && snapshot && user.scoringSex !== snapshot;
                  return (
                    <li key={w.id} className="admin__row">
                      <div className="admin__info">
                        <div className="admin__line1">
                          <span className="admin__name">{user?.displayName ?? 'Unknown User'}</span>
                          <span className={`admin__sex ${mismatch ? 'admin__sex--mismatch' : ''}`}>
                            {sexLabel(snapshot)}
                            {mismatch && (
                              <span title={`Profile now says ${sexLabel(user?.scoringSex)}`}>
                                {' '}
                                ⚠
                              </span>
                            )}
                          </span>
                          <span className="admin__date">{formatDate(w.workoutDate)}</span>
                        </div>
                        <div className="admin__line2">
                          <span className="admin__exercise">{w.exerciseNameSnapshot}</span>
                          <span className="admin__load">{formatLoadAndReps(w)}</span>
                        </div>
                        <div className="admin__line3">
                          <span>{CATEGORY_LABELS[w.category] ?? w.category}</span>
                          <span>{Math.round(w.points)} pts</span>
                          <span>{Math.round(w.estimated1RmLb)} lb 1RM</span>
                          <span>{Math.round(w.bodyWeightLbSnapshot)} lb BW</span>
                        </div>
                      </div>

                      {armedId === w.id ? (
                        <div className="admin__confirm">
                          <button
                            type="button"
                            className="admin__delete admin__delete--confirm"
                            onClick={() => handleDelete(w)}
                            disabled={deletingId === w.id}
                          >
                            {deletingId === w.id ? '...' : 'Sure?'}
                          </button>
                          <button
                            type="button"
                            className="admin__cancel"
                            onClick={() => setArmedId(null)}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="admin__delete"
                          onClick={() => setArmedId(w.id)}
                          aria-label={`Delete ${user?.displayName ?? 'this'} ${w.exerciseNameSnapshot} on ${formatDate(w.workoutDate)}`}
                        >
                          ×
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>

              {filtered.length > visible && (
                <button
                  type="button"
                  className="admin__more"
                  onClick={() => setVisible((n) => n + PAGE_SIZE)}
                >
                  Show more ({filtered.length - visible} older)
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
